import { Injectable } from '@angular/core';
import { params } from '@app/configurations';
import { ApiService } from '@app/core/api/api.service';
import { ApiCommonRoutes, ApiEndpoint } from '@app/core/models/api.models';
import { GlobalFilterType } from '@app/core/models/common.models';
import { SubFiltersService } from '@app/core/services/subfilters.service';
import { AccountsState } from '@app/core/store/states/accounts/accounts.state';
import { GlobalFilterNotFound } from '@app/core/store/states/global-filters/global-filters-state.actions';
import { GlobalFiltersState } from '@app/core/store/states/global-filters/global-filters.state';
import { RouterNavState } from '@app/core/store/states/router-nav/router-nav.state';
import { encodeSearchTerms } from '@app/core/utilities/url.utilities';
import { ForcedFilter } from '@app/shared/components';
import { defaultToastError } from '@app/shared/operators/default-toast-error.operator';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { catchError, map, Observable, of, switchMap, tap } from 'rxjs';
import { ListItem } from '../models/selector.model';
import { BaseSelectorService } from './base-selector.service';
import { CriteriaDTO, getCriteriaFilters } from './global-filters.utils';

@Injectable()
export class CriteriaSelectorService extends BaseSelectorService {
  requiredParams = [params.GLOBAL_FILTER_CRITERIA_ID, params.GLOBAL_FILTER_CRITERIA_QUERY, null, null];
  includedData: any = [
    'campaignName',
    'campaignType',
    'adgroupName',
    'adspertAdgroupId',
    'criterionName',
    'criterionId',
    'criterionStatus',
  ];
  defaultLabel = 'global-filter.criteria-selector.placeholder';
  defaultSubfiltersType = 'criteriaDefaultFilter';
  dto = CriteriaDTO;

  readonly srcSelectedItem$ = this.getSelectedItemStream(params.GLOBAL_FILTER_CRITERIA_ID);

  readonly subfilterOptions = () => this.subFiltersService.getCriteriaSubFilters();

  getSelectedItem(id): Observable<ListItem> {
    if (this.cache.items?.[id]) return of(this.cache.items[id]);
    this.loader$.next(true);
    return of(this.account).pipe(
      switchMap((account) =>
        this.apiService
          .getObject({
            endpoint: ApiEndpoint.Accounts,
            include: this.includedData,
            route: [account.adspertAccountId, ApiCommonRoutes.Criteria, id],
          })
          .pipe(
            map(({ data }: any) => {
              this.loader$.next(false);
              this.cache.items[data.criterionId] = this.dto({
                ...data,
                accountPlatform: account.platform,
                accountName: account.accountName,
                accountId: account.accountId,
              });
              return this.cache.items[data.criterionId];
            }),
            defaultToastError(
              'Failed to fetch selected Criteria in the Global Filter. Please try with another selection.',
            ),
            catchError((err) => {
              this.store.dispatch(new GlobalFilterNotFound(GlobalFilterType.CRITERIA));
              this.loader$.next(false);
              return of({});
            }),
          ),
      ),
    );
  }

  getItems(
    { query, filters = [] },
    offset = 0,
    forDestination?: boolean,
    forcedFilters?: ForcedFilter,
  ): Observable<{ items: ListItem[]; count: number }> {
    this.listLoader$.next(true);
    const cq = params.GLOBAL_FILTER_CAMPAIGN_QUERY;
    const adq = params.GLOBAL_FILTER_AD_GROUPS_QUERY;
    const state = this.store.selectSnapshot(RouterNavState.queryParams);

    let tmpFilter;
    const tempC =
      state[cq] !== undefined
        ? state[cq]
        : this.store.selectSnapshot(GlobalFiltersState.filtersValues).campaignsDefaultFilter;
    tmpFilter = this.subFiltersService.getFiltersForAPI(tempC, this.subFiltersService.getCampaignSubFilters()) ?? [];

    const tempAd =
      state[adq] !== undefined
        ? state[adq]
        : this.store.selectSnapshot(GlobalFiltersState.filtersValues).adGroupsDefaultFilter;
    tmpFilter = this.subFiltersService.getFiltersForAPI(tempAd, this.subFiltersService.getAdGroupsSubFilters()) ?? [];

    const refiners: any = {
      sortValues: { criterionStatus: 1, criterionName: 2 },
      filterValues: {
        ...getCriteriaFilters(
          [...filters, ...tmpFilter].filter(([k]) => k.startsWith('filter_')),
          this.store.selectSnapshot(GlobalFiltersState.filtersValues),
          state,
        ),
        ...this.getForcedFilters(forcedFilters, forDestination),
      },
    };

    if (!!query) {
      refiners.searchFields =
        'performanceGroupName,performanceGroupId,amzPortfolioId,amzPortfolioName,campaignId,campaignName,adgroupId,adgroupName,criterionName,criterionId';
      refiners.searchTerm = encodeSearchTerms(query);
    }
    const account = this.account;
    return this.apiService
      .getList({
        endpoint: ApiEndpoint.Accounts,
        route: [account.adspertAccountId, ApiCommonRoutes.Criteria],
        include: this.includedData,
        ...refiners,
        searchValues: this.getSearchValues(filters),
        limit: this.limit,
        offset: offset,
      })
      .pipe(
        tap(() => this.listLoader$.next(false)),
        map(({ data, meta }) => {
          data.forEach((d: any) => {
            d.accountPlatform = account.platform;
            d.accountName = account.accountName;
            d.accountId = account.accountId;
          });
          const items = data.map(this.dto);
          return { items, count: meta.pagination.total };
        }),
        defaultToastError('Failed to fetch Ad Groups. Please try with another selection.'),
        catchError((err) => {
          this.listLoader$.next(false);
          return of({ items: [], count: 0 });
        }),
      );
  }

  constructor(
    translate: TranslateService,
    store: Store,
    apiService: ApiService,
    private subFiltersService: SubFiltersService,
  ) {
    super(translate, store, apiService);
  }

  private get account() {
    const state = this.store.selectSnapshot(RouterNavState.queryParams);
    const adId = params.GLOBAL_FILTER_AD_GROUPS_ID;
    return (
      this.store.selectSnapshot(AccountsState.getActiveAccount) ||
      this.store.selectSnapshot(AccountsState.getSelectedAccount(state[adId]?.split(':')?.[0]))
    );
  }
}
